import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import Layout, { StatusBadge } from '../components/Layout'
import { Card, Empty, Stat, money, fmtDate, fmtDateTime } from '../components/ui'
import { clientLabel, type Client, type Invoice, type Meeting, type Project, type Video, reviewTarget } from '../lib/db'
import { api } from '../lib/api'

interface ContactReq { id: string; name: string; email: string; phone: string; company: string; project_type: string; message: string; created_at: string; handled: boolean; page?: string }
interface ReviewLink { token: string; path: string; url: string; created_at: string; stats: { comments: number; unresolved: number; approvals: number; last_comment_at: string | null }; recent: { id: string; file: string; author: string; text: string; time: number | null; resolved: boolean; created_at: string }[] }

export default function Admin() {
  const [clients, setClients] = useState<Client[]>([])
  const [projects, setProjects] = useState<Project[]>([])
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [videos, setVideos] = useState<Video[]>([])
  const [meetings, setMeetings] = useState<Meeting[]>([])
  const [leads, setLeads] = useState(0)
  const [contacts, setContacts] = useState<ContactReq[]>([])
  const [openReq, setOpenReq] = useState<string | null>(null)
  const loadContacts = () => api<{ contacts: ContactReq[] }>('/contacts').then(j => setContacts(j.contacts)).catch(() => {})
  const [reviews, setReviews] = useState<ReviewLink[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    (async () => {
      const [c, p, i, v, m, l] = await Promise.all([
        supabase.from('clients').select('*').order('created_at', { ascending: false }),
        supabase.from('projects').select('*').order('created_at', { ascending: false }),
        supabase.from('invoices').select('*').order('created_at', { ascending: false }),
        supabase.from('videos').select('*').order('created_at', { ascending: false }),
        supabase.from('meetings').select('*').gte('meeting_date', new Date(Date.now() - 3600e3).toISOString()).neq('status', 'annulé').order('meeting_date').limit(6),
        supabase.from('leads').select('id', { count: 'exact', head: true }),
      ])
      setClients((c.data as any) || []); setProjects((p.data as any) || []); setInvoices((i.data as any) || [])
      setVideos((v.data as any) || []); setMeetings((m.data as any) || []); setLeads(l.count || 0); setLoading(false)
    })()
    loadContacts()
    api<{ reviews: ReviewLink[] }>('/stock/reviews').then(j => setReviews(j.reviews)).catch(() => {})
  }, [])
  const fmtTc = (t: number) => `${Math.floor(t / 60)}:${String(Math.floor(t % 60)).padStart(2, '0')}`

  async function toLead(c: ContactReq) {
    const { error } = await supabase.from('leads').insert({ company_name: c.company || c.name, contact_name: c.name, email: c.email, phone: c.phone || null, status: 'nouveau', source: 'site web', notes: `${c.project_type}\n\n${c.message}` })
    if (error) { alert('Erreur : ' + error.message); return }
    await api('/contacts/handled', { method: 'POST', json: { id: c.id, handled: true } }); loadContacts(); setLeads(n => n + 1)
  }
  async function handled(c: ContactReq, v: boolean) { await api('/contacts/handled', { method: 'POST', json: { id: c.id, handled: v } }); loadContacts() }
  async function delReq(c: ContactReq) { if (!confirm('Supprimer cette demande ?')) return; await api(`/contacts?id=${c.id}`, { method: 'DELETE' }); loadContacts() }
  const pending = contacts.filter(c => !c.handled)

  const cname = (id: string | null) => clientLabel(clients.find(c => c.id === id))
  const active = projects.filter(p => !['livré', 'annulé'].includes(p.status))
  const open = invoices.filter(i => ['envoyée', 'en retard'].includes(i.status))
  const overdue = open.filter(i => i.due_date && i.due_date < new Date().toISOString().slice(0, 10))
  const toReview = videos.filter(v => v.review_status !== 'approuve')
  const noAccount = clients.filter(c => !c.profile_id)
  const btn = 'px-4 py-2 rounded-sm text-xs font-bold uppercase tracking-widest bg-white/5 hover:bg-white/10 border border-white/10 transition-colors'

  return (
    <Layout title="Tableau de bord" actions={<>
      <a href="/admin/clients?new=1" className={btn}>+ Client</a>
      <a href="/admin/projets?new=1" className={btn}>+ Projet</a>
      <a href="/admin/comptabilite?tab=factures&new=1" className={btn}>+ Facture</a>
      <a href="/admin/rencontres?new=1" className={btn}>+ Rencontre</a>
    </>}>
      {loading ? <p className="text-acier">Chargement…</p> : (
        <div className="space-y-8">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <Stat label="Clients" value={clients.length} sub={noAccount.length ? `${noAccount.length} sans compte` : 'tous ont un compte'} href="/admin/clients" />
            <Stat label="Projets actifs" value={active.length} sub={`${projects.length} au total`} href="/admin/projets" />
            <Stat label="À encaisser" value={money(open.reduce((s, i) => s + Number(i.amount), 0))} sub={overdue.length ? `${overdue.length} en retard` : `${open.length} facture(s)`} href="/admin/comptabilite?tab=factures" />
            <Stat label="Vidéos en revue" value={toReview.length} sub={`${videos.length} vidéo(s)`} href="/admin/livrables" />
            <Stat label="Leads" value={leads} href="/admin/leads" />
          </div>

          {contacts.length > 0 && (
            <Card title={`Demandes du site web${pending.length ? ` (${pending.length} à traiter)` : ''}`}>
              {contacts.slice(0, 15).map(c => (
                <div key={c.id} className={`py-3 border-t border-white/5 first:border-0 text-sm ${c.handled ? 'opacity-50' : ''}`}>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <button onClick={() => setOpenReq(openReq === c.id ? null : c.id)} className="text-left min-w-0 flex-1 hover:text-orange">
                      <span className="font-medium">{c.company || c.name}</span>{c.company && <span className="text-acier"> · {c.name}</span>}
                      <div className="text-xs text-acier">{c.project_type} · {fmtDateTime(c.created_at)}{c.page ? ` · via ${c.page}` : ''}</div>
                    </button>
                    <div className="flex items-center gap-3 text-xs uppercase tracking-widest whitespace-nowrap">
                      <a href={`mailto:${c.email}?subject=${encodeURIComponent('Votre demande à Pivot Média')}`} className="text-orange hover:underline">Répondre</a>
                      {!c.handled && <button onClick={() => toLead(c)} className="text-acier hover:text-orange">→ Lead</button>}
                      <button onClick={() => handled(c, !c.handled)} className="text-acier hover:text-green-300">{c.handled ? 'Rouvrir' : 'Traité'}</button>
                      <button onClick={() => delReq(c)} className="text-acier hover:text-red-300">×</button>
                    </div>
                  </div>
                  {openReq === c.id && <div className="mt-2 text-acier whitespace-pre-line border-l-2 border-orange/50 pl-3">{c.email}{c.phone ? ` · ${c.phone}` : ''}{'\n\n'}{c.message}</div>}
                </div>
              ))}
            </Card>
          )}

          {reviews.length > 0 && (
            <Card title={`Revues en cours${reviews.some(r => r.stats.unresolved) ? ` (${reviews.reduce((n, r) => n + r.stats.unresolved, 0)} commentaire(s) à traiter)` : ''}`} actions={<a href="/admin/livrables" className="text-xs uppercase tracking-widest text-orange hover:underline">Livrables & revue</a>}>
              {reviews.slice(0, 8).map(r => (
                <div key={r.token} className="py-3 border-t border-white/5 first:border-0 text-sm">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <span className="font-medium">💬 {r.path.split('/').pop()}</span>
                      <div className="text-xs text-acier">{r.stats.comments} commentaire{r.stats.comments > 1 ? 's' : ''}{r.stats.unresolved ? <span className="text-orange"> · {r.stats.unresolved} à traiter</span> : ''} · {r.stats.approvals} approuvé{r.stats.approvals > 1 ? 's' : ''}{r.stats.last_comment_at ? ` · dernier ${fmtDateTime(r.stats.last_comment_at)}` : ''}</div>
                    </div>
                    <a href={r.url} target="_blank" rel="noreferrer" className="text-orange text-xs uppercase tracking-widest hover:underline">Ouvrir la revue</a>
                  </div>
                  {r.recent.filter(c => !c.resolved).slice(0, 3).map(c => (
                    <div key={c.id} className="mt-1.5 text-xs text-acier truncate border-l-2 border-white/10 pl-2"><span className="text-blanc">{c.author}</span>{c.time != null ? ` à ${fmtTc(c.time)}` : ''} sur {reviewTarget(c.file)} : {c.text}</div>
                  ))}
                </div>
              ))}
            </Card>
          )}

          <div className="grid lg:grid-cols-2 gap-6">
            <Card title="Prochaines rencontres" actions={<a href="/admin/rencontres" className="text-xs uppercase tracking-widest text-orange hover:underline">Tout voir</a>}>
              {meetings.length === 0 ? <Empty>Aucune rencontre à venir.</Empty> : meetings.map(m => (
                <div key={m.id} className="flex items-center justify-between py-2 border-t border-white/5 first:border-0 text-sm gap-3">
                  <div className="min-w-0"><div className="truncate">{m.title}</div><div className="text-xs text-acier">{cname(m.client_id)} · {m.platform}</div></div>
                  <div className="text-right text-xs whitespace-nowrap"><div className="text-orange">{fmtDateTime(m.meeting_date)}</div>{m.meeting_url && <a href={m.meeting_url} target="_blank" rel="noreferrer" className="text-acier hover:text-orange">Rejoindre</a>}</div>
                </div>
              ))}
            </Card>

            <Card title="Projets en cours" actions={<a href="/admin/projets" className="text-xs uppercase tracking-widest text-orange hover:underline">Tout voir</a>}>
              {active.length === 0 ? <Empty>Aucun projet actif.</Empty> : active.slice(0, 8).map(p => (
                <a key={p.id} href={`/admin/projets?edit=${p.id}`} className="flex items-center justify-between py-2 border-t border-white/5 first:border-0 text-sm gap-3 hover:text-orange">
                  <div className="min-w-0"><div className="truncate">{p.title}</div><div className="text-xs text-acier">{cname(p.client_id)}{p.delivery_date ? ` · livraison ${fmtDate(p.delivery_date)}` : ''}</div></div>
                  <StatusBadge status={p.status} />
                </a>
              ))}
            </Card>

            <Card title="Factures à suivre" actions={<a href="/admin/comptabilite?tab=factures" className="text-xs uppercase tracking-widest text-orange hover:underline">Tout voir</a>}>
              {open.length === 0 ? <Empty>Rien à encaisser pour l'instant.</Empty> : open.slice(0, 8).map(i => (
                <a key={i.id} href={`/admin/comptabilite?tab=factures&edit=${i.id}`} className="flex items-center justify-between py-2 border-t border-white/5 first:border-0 text-sm gap-3 hover:text-orange">
                  <div className="min-w-0"><div className="truncate">{i.invoice_number || 'Facture'} · {cname(i.client_id)}</div><div className="text-xs text-acier">échéance {fmtDate(i.due_date)}</div></div>
                  <div className="flex items-center gap-3 whitespace-nowrap">{money(i.amount)}<StatusBadge status={overdue.includes(i) ? 'en retard' : i.status} /></div>
                </a>
              ))}
            </Card>

            <Card title="Vidéos en attente de revue" actions={<a href="/admin/livrables" className="text-xs uppercase tracking-widest text-orange hover:underline">Tout voir</a>}>
              {toReview.length === 0 ? <Empty>Aucune vidéo en attente.</Empty> : toReview.slice(0, 8).map(v => (
                <a key={v.id} href={`/revue/${v.id}`} className="flex items-center justify-between py-2 border-t border-white/5 first:border-0 text-sm gap-3 hover:text-orange">
                  <div className="min-w-0"><div className="truncate">🎬 {v.title}</div><div className="text-xs text-acier">{cname(v.client_id)}</div></div>
                  <StatusBadge status={v.review_status} />
                </a>
              ))}
            </Card>
          </div>

          {noAccount.length > 0 && (
            <Card title="Clients sans compte portail">
              <div className="flex flex-wrap gap-2">
                {noAccount.map(c => <a key={c.id} href={`/admin/clients/${c.id}`} className="text-xs px-3 py-1.5 rounded bg-yellow-500/10 text-yellow-300 hover:bg-yellow-500/20">{clientLabel(c)} · inviter →</a>)}
              </div>
            </Card>
          )}
        </div>
      )}
    </Layout>
  )
}
